/**
 * Smart API Router Service
 *
 * This service implements intelligent API routing based on authentication status:
 * - Uses OIDC authentication state to determine auth status
 * - Routes to /api/v1/private/* for authenticated users
 * - Routes to /api/v1/public/* for unauthenticated users
 * - Enables different caching strategies for public vs private APIs
 */

import { API_CONFIG } from "../config/apiConfig";
import { getUser } from "../auth/oidcConfig";

export interface AuthState {
  isAuthenticated: boolean;
  lastChecked: number;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  organization?: {
    id: string;
    name: string;
    subdomain: string;
  };
}

export interface ApiRouteConfig {
  baseUrl: string;
  cachingStrategy: "public" | "private";
  prefix: "/api/v1/public" | "/api/v1/private";
}

class SmartApiRouter {
  private authState: AuthState | null = null;
  private sessionCheckPromise: Promise<AuthState> | null = null;
  private readonly SESSION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private previousAuthState: boolean | null = null; // Track previous auth state for reload detection

  /**
   * Get the current authentication state
   * This is the main method that checks OIDC user and returns auth status
   */
  async getAuthState(): Promise<AuthState> {
    // Return cached state if still valid
    if (this.authState && this.isStateValid(this.authState)) {
      return this.authState;
    }

    // If already checking session, return existing promise
    if (this.sessionCheckPromise) {
      return this.sessionCheckPromise;
    }

    // Create new session check promise
    this.sessionCheckPromise = this.checkSessionEndpoint();

    try {
      this.authState = await this.sessionCheckPromise;
      return this.authState;
    } finally {
      this.sessionCheckPromise = null;
    }
  }

  /**
   * Get the appropriate API configuration based on auth state
   */
  async getApiConfig(): Promise<ApiRouteConfig> {
    const authState = await this.getAuthState();

    // Get base URL without /api/v1 prefix
    const baseUrl = API_CONFIG.app.apiUrl.replace("/api/v1", "");

    if (authState.isAuthenticated) {
      return {
        baseUrl: `${baseUrl}/api/v1/private`,
        cachingStrategy: "private",
        prefix: "/api/v1/private",
      };
    } else {
      return {
        baseUrl: `${baseUrl}/api/v1/public`,
        cachingStrategy: "public",
        prefix: "/api/v1/public",
      };
    }
  }

  /**
   * Build a complete API URL with the appropriate prefix
   */
  async buildApiUrl(endpoint: string): Promise<string> {
    const config = await this.getApiConfig();

    // Remove leading slash if present to avoid double slashes
    let cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;

    // Remove any existing /api/v1 prefix to avoid double prefixes
    if (cleanEndpoint.startsWith("api/v1/")) {
      cleanEndpoint = cleanEndpoint.replace("api/v1/", "");
    }

    // Build the full URL
    return `${config.baseUrl}/${cleanEndpoint}`;
  }

  /**
   * Get caching headers based on auth state
   * Note: Cache-Control header removed to avoid CORS issues in AWS environment
   */
  async getCachingHeaders(): Promise<Record<string, string>> {
    const config = await this.getApiConfig();

    if (config.cachingStrategy === "public") {
      return {
        // "Cache-Control": "public, max-age=300", // Removed: causes CORS issues in AWS
        "X-API-Mode": "public",
      };
    } else {
      return {
        // "Cache-Control": "private, no-cache", // Removed: causes CORS issues in AWS
        "X-API-Mode": "private",
      };
    }
  }

  /**
   * Force refresh auth state (useful for login/logout events)
   */
  async refreshAuthState(): Promise<AuthState> {
    this.authState = null;
    this.sessionCheckPromise = null;
    return this.getAuthState();
  }

  /**
   * Get current auth state without triggering a refresh (for UI updates)
   */
  getCurrentAuthState(): AuthState | null {
    return this.authState;
  }

  /**
   * Check if the cached auth state is still valid
   */
  private isStateValid(state: AuthState): boolean {
    const now = Date.now();
    return now - state.lastChecked < this.SESSION_CACHE_DURATION;
  }

  /**
   * Check OIDC authentication status
   * Uses oidc-client-ts to get user from storage
   */
  private async checkSessionEndpoint(): Promise<AuthState> {
    try {
      console.log("🔍 Checking OIDC authentication state...");

      // Get user from OIDC client storage
      const oidcUser = await getUser();

      if (oidcUser && !this.isTokenExpired(oidcUser)) {
        console.log("✅ OIDC user authenticated - using private APIs");
        console.log(
          "👤 User:",
          oidcUser.profile?.email || oidcUser.profile?.sub
        );

        const newAuthState: AuthState = {
          isAuthenticated: true,
          lastChecked: Date.now(),
          user: {
            id: (oidcUser.profile as any)?.uid || oidcUser.profile?.sub || "",
            name: (oidcUser.profile as any)?.name || "",
            email: (oidcUser.profile as any)?.email || "",
          },
          organization: {
            id: (oidcUser.profile as any)?.org_id || "",
            name: (oidcUser.profile as any)?.org_name || "",
            subdomain: (oidcUser.profile as any)?.subdomain || "",
          },
        };

        // Update previous auth state for next comparison
        this.previousAuthState = true;

        return newAuthState;
      } else {
        console.log("❌ No valid OIDC session - using public APIs");

        const newAuthState = {
          isAuthenticated: false,
          lastChecked: Date.now(),
        };

        // Check if user was previously authenticated and now is not
        this.handleAuthStateTransition(false);

        return newAuthState;
      }
    } catch (error) {
      console.log("❌ OIDC auth check error - using public APIs:", error);

      const newAuthState = {
        isAuthenticated: false,
        lastChecked: Date.now(),
      };

      // Check if user was previously authenticated and now is not
      this.handleAuthStateTransition(false);

      return newAuthState;
    }
  }

  /**
   * Check if OIDC token is expired
   */
  private isTokenExpired(user: any): boolean {
    if (!user.expires_at) return false;
    const now = Math.floor(Date.now() / 1000);
    return user.expires_at <= now;
  }

  /**
   * Handle authentication state transitions and reload browser if needed
   */
  private handleAuthStateTransition(newAuthState: boolean): void {
    // Only trigger reload if user was authenticated before and now is not
    if (this.previousAuthState === true && newAuthState === false) {
      console.log("🔄 User logged out - reloading page to load public data");

      // Small delay to ensure logs are visible
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }

    // Update previous state for next comparison
    this.previousAuthState = newAuthState;
  }

  /**
   * Subscribe to auth state changes (for UI updates)
   */
  onAuthStateChange(callback: (authState: AuthState) => void): () => void {
    const interval = setInterval(async () => {
      const currentState = await this.getAuthState();
      callback(currentState);
    }, 30000); // Check every 30 seconds

    // Return cleanup function
    return () => clearInterval(interval);
  }
}

// Export singleton instance
export const smartApiRouter = new SmartApiRouter();

// Helper functions for easy usage
export const getApiUrl = (endpoint: string) =>
  smartApiRouter.buildApiUrl(endpoint);
export const getAuthState = () => smartApiRouter.getAuthState();
export const refreshAuth = () => smartApiRouter.refreshAuthState();
export const getCachingHeaders = () => smartApiRouter.getCachingHeaders();

export default smartApiRouter;
